import * as React from 'react';
import { cn } from '../../../lib/utils';
import { useIsMobile } from '../../../hooks/use-mobile';

/** 相对原图自然宽高的归一化裁切框（0–1），仅 rotation=0 时可计算 */
export type ImageCropperNormRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export interface ImageCropperRef {
  /**
   * 放大图片
   */
  zoomIn: () => void;
  /**
   * 缩小图片
   */
  zoomOut: () => void;
  /**
   * 逆时针旋转90度
   */
  rotateCCW: () => void;
  /**
   * 顺时针旋转90度
   */
  rotateCW: () => void;
  /**
   * 完成裁切（导出像素文件）
   */
  complete: () => Promise<void>;
  /**
   * 读取当前裁切框在原图上的归一化坐标（非 0° 旋转时返回 null）
   */
  getNormalizedCropRect: () => ImageCropperNormRect | null;
}

export interface ImageCropperProps {
  /**
   * 图片源（URL 或 base64）
   */
  image: string;
  /**
   * 裁切模式
   * - 'avatar': 头像模式，圆形裁切，1:1 宽高比
   * - 'free': 自由裁切，无宽高比限制
   * - 'custom': 自定义宽高比
   */
  mode?: 'avatar' | 'free' | 'custom';
  /**
   * 自定义宽高比（仅在 mode='custom' 时生效）
   * 例如：16/9 表示 16:9
   */
  aspectRatio?: number;
  /**
   * 最小缩放级别
   * @default 1
   */
  minZoom?: number;
  /**
   * 最大缩放级别
   * @default 3
   */
  maxZoom?: number;
  /**
   * 缩放灵敏度（按钮/键盘步进）
   * @default 0.1
   */
  zoomSensitivity?: number;
  /**
   * 滚轮缩放灵敏度（建议小于 zoomSensitivity，便于精细控制）
   * @default 0.03
   */
  wheelZoomSensitivity?: number;
  /**
   * 裁切区域最小内边距（像素）
   * @default 25
   */
  cropPadding?: number;
  /**
   * 键盘移动步长（像素）
   * @default 10
   */
  keyboardStep?: number;
  /**
   * 裁切区域变化回调
   */
  onCropChange?: (crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  /**
   * 完成裁切回调，返回裁切后的图片文件
   */
  onComplete?: (file: File) => void;
  /**
   * 裁切区域变化时实时预览回调，返回当前裁切区域的 data URL（用于头像预览区自动刷新）
   */
  onCropPreview?: (dataUrl: string) => void;
  /**
   * 取消回调
   */
  onCancel?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 输出图片质量（0-1）
   * @default 0.9
   */
  quality?: number;
  /**
   * 输出图片格式
   * @default 'image/jpeg'
   */
  outputFormat?: 'image/jpeg' | 'image/png' | 'image/webp';
  /** 进入裁切器时回显上次裁切区域（归一化坐标） */
  initialNormalizedCrop?: ImageCropperNormRect | null;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 图片裁切组件
 * 支持移动端和PC端，提供头像裁切和一般图片裁切功能
 */
export const ImageCropper = React.forwardRef<ImageCropperRef, ImageCropperProps>(
  (
    {
      image,
      mode = 'free',
      aspectRatio,
  minZoom = 0.1,
  maxZoom = 10,
      zoomSensitivity = 0.1,
      wheelZoomSensitivity = 0.03,
      cropPadding = 25,
      keyboardStep = 10,
      onCropChange,
      onComplete,
      onCropPreview,
      onCancel,
      className,
      quality = 0.9,
      outputFormat = 'image/jpeg',
      initialNormalizedCrop = null,
    },
    ref
  ) => {
    const containerDivRef = React.useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const imageRef = React.useRef<HTMLImageElement>(null);
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
    const [zoom, setZoom] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);
    const [imagePosition, setImagePosition] = React.useState({ x: 0, y: 0 });
    const [cropArea, setCropArea] = React.useState<CropArea>({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    });
    const [isDragging, setIsDragging] = React.useState(false);
    const [isResizing, setIsResizing] = React.useState(false);
    const [isMovingImage, setIsMovingImage] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const [resizeHandle, setResizeHandle] = React.useState<string | null>(null);
    const hasRestoredInitialCropRef = React.useRef(false);
    const zoomRef = React.useRef(zoom);
    zoomRef.current = zoom;
    const imagePositionRef = React.useRef(imagePosition);
    imagePositionRef.current = imagePosition;

    // 计算实际宽高比
    const actualAspectRatio = React.useMemo(() => {
      if (mode === 'avatar') return 1;
      if (mode === 'custom' && aspectRatio) return aspectRatio;
      return undefined; // free mode
    }, [mode, aspectRatio]);

    const computeCropSize = React.useCallback(
      (containerWidth: number, containerHeight: number) => {
        const padding = cropPadding * 2;
        let cropWidth: number;
        let cropHeight: number;

        if (actualAspectRatio) {
          if (containerWidth / containerHeight > actualAspectRatio) {
            cropHeight = containerHeight - padding;
            cropWidth = cropHeight * actualAspectRatio;
          } else {
            cropWidth = containerWidth - padding;
            cropHeight = cropWidth / actualAspectRatio;
          }
        } else {
          cropWidth = (containerWidth - padding) * 0.8;
          cropHeight = (containerHeight - padding) * 0.8;
        }

        return {
          width: Math.max(50, cropWidth),
          height: Math.max(50, cropHeight),
        };
      },
      [actualAspectRatio, cropPadding],
    );

    /**
     * 固定比例（custom + aspectRatio）：裁切框宽度与图片在视口中的显示宽度一致，
     * 竖图两侧不再出现「虚线比图宽」需用户手动缩窄的情况。
     */
    const computeFixedAspectCropToImageDisplay = React.useCallback(
      (
        containerW: number,
        containerH: number,
        naturalW: number,
        naturalH: number,
        zoomLevel: number,
        posX: number,
        posY: number,
        aspect: number,
      ): CropArea => {
        const displayW = naturalW * zoomLevel;
        const displayH = naturalH * zoomLevel;
        const imageLeft = (containerW - displayW) / 2 + posX;
        const imageTop = (containerH - displayH) / 2 + posY;

        let cropW = Math.max(50, displayW);
        let cropH = cropW / aspect;
        if (cropH > displayH) {
          cropH = Math.max(50, displayH);
          cropW = Math.max(50, cropH * aspect);
        }

        let cropX = imageLeft + (displayW - cropW) / 2;
        let cropY = imageTop + (displayH - cropH) / 2;

        cropX = Math.max(0, Math.min(cropX, containerW - cropW));
        cropY = Math.max(0, Math.min(cropY, containerH - cropH));

        return { x: cropX, y: cropY, width: cropW, height: cropH };
      },
      [],
    );

    const sanitizeInitialCrop = React.useCallback((crop: ImageCropperNormRect | null | undefined) => {
      if (!crop) return null;
      const xx = Number(crop.x);
      const yy = Number(crop.y);
      const ww = Number(crop.w);
      const hh = Number(crop.h);
      if (![xx, yy, ww, hh].every((n) => Number.isFinite(n))) return null;
      if (ww <= 0 || hh <= 0) return null;
      const x = Math.min(1, Math.max(0, xx));
      const y = Math.min(1, Math.max(0, yy));
      let w = Math.min(1, Math.max(0.001, ww));
      let h = Math.min(1, Math.max(0.001, hh));
      if (x + w > 1) w = 1 - x;
      if (y + h > 1) h = 1 - y;
      if (w <= 0.001 || h <= 0.001) return null;
      return { x, y, w, h };
    }, []);

    // 图片加载完成
    const handleImageLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      setImageLoaded(true);

      if (containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        /**
         * 进入裁切视口时按“完整可见”规则：
         * - 若原图宽 >= 高：图片宽 = 视口宽，高度按比例
         * - 若原图高 > 宽：图片高 = 视口高，宽度按比例
         * 这样能完整看到整张图，不做放大裁切。
         */
        const initialZoom =
          img.naturalWidth >= img.naturalHeight
            ? containerWidth / Math.max(1, img.naturalWidth)
            : containerHeight / Math.max(1, img.naturalHeight);
        setZoom(initialZoom);
        setImagePosition({ x: 0, y: 0 });

        const restored = sanitizeInitialCrop(initialNormalizedCrop);
        if (restored) {
          const displayW = img.naturalWidth * initialZoom;
          const displayH = img.naturalHeight * initialZoom;
          const imageLeft = (containerWidth - displayW) / 2;
          const imageTop = (containerHeight - displayH) / 2;
          const next = {
            x: imageLeft + restored.x * displayW,
            y: imageTop + restored.y * displayH,
            width: restored.w * displayW,
            height: restored.h * displayH,
          };
          setCropArea(next);
          hasRestoredInitialCropRef.current = true;
        } else if (mode === 'custom' && actualAspectRatio) {
          const next = computeFixedAspectCropToImageDisplay(
            containerWidth,
            containerHeight,
            img.naturalWidth,
            img.naturalHeight,
            initialZoom,
            0,
            0,
            actualAspectRatio,
          );
          setCropArea(next);
          hasRestoredInitialCropRef.current = false;
        } else {
          const size = computeCropSize(containerWidth, containerHeight);
          setCropArea({
            x: (containerWidth - size.width) / 2,
            y: (containerHeight - size.height) / 2,
            width: size.width,
            height: size.height,
          });
          hasRestoredInitialCropRef.current = false;
        }
      }
    }, [
      computeCropSize,
      computeFixedAspectCropToImageDisplay,
      initialNormalizedCrop,
      mode,
      actualAspectRatio,
      sanitizeInitialCrop,
    ]);

    // 固定比例 + custom：按「与图片显示等宽」规则布局，并在容器尺寸变化时重算
    React.useEffect(() => {
      if (!imageLoaded || !containerRef.current || !imageRef.current) return;
      if (mode !== 'custom' || !actualAspectRatio) return;
      if (sanitizeInitialCrop(initialNormalizedCrop)) return;

      const run = () => {
        const container = containerRef.current;
        const img = imageRef.current;
        if (!container || !img?.naturalWidth) return;
        const pos = imagePositionRef.current;
        const next = computeFixedAspectCropToImageDisplay(
          container.clientWidth,
          container.clientHeight,
          img.naturalWidth,
          img.naturalHeight,
          zoomRef.current,
          pos.x,
          pos.y,
          actualAspectRatio,
        );
        setCropArea(next);
      };

      run();
      const el = containerRef.current;
      const ro = new ResizeObserver(run);
      ro.observe(el);
      return () => ro.disconnect();
    }, [
      actualAspectRatio,
      computeFixedAspectCropToImageDisplay,
      imageLoaded,
      initialNormalizedCrop,
      mode,
      sanitizeInitialCrop,
    ]);

    // 比例切换时实时更新选择框尺寸（保持中心点尽量不变）；custom+固定比例由上一段 effect 处理
    React.useEffect(() => {
      if (!imageLoaded || !containerRef.current) return;
      if (mode === 'custom' && actualAspectRatio) return;
      if (hasRestoredInitialCropRef.current && mode === 'free' && !aspectRatio) return;
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const size = computeCropSize(containerWidth, containerHeight);

      setCropArea((prev) => {
        const centerX = prev.x + prev.width / 2;
        const centerY = prev.y + prev.height / 2;
        const nextX = Math.min(
          Math.max(0, centerX - size.width / 2),
          Math.max(0, containerWidth - size.width),
        );
        const nextY = Math.min(
          Math.max(0, centerY - size.height / 2),
          Math.max(0, containerHeight - size.height),
        );
        return {
          x: nextX,
          y: nextY,
          width: size.width,
          height: size.height,
        };
      });
      hasRestoredInitialCropRef.current = false;
    }, [actualAspectRatio, aspectRatio, imageLoaded, computeCropSize, mode]);

    // 鼠标/触摸事件处理
    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        if (!containerRef.current || !imageRef.current) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        // 检查是否点击在裁切框内
        const isInCropArea =
          x >= cropArea.x &&
          x <= cropArea.x + cropArea.width &&
          y >= cropArea.y &&
          y <= cropArea.y + cropArea.height;

        // 检查是否点击在调整手柄上
        const handleSize = 10;
        const handles = {
          nw: { x: cropArea.x, y: cropArea.y },
          ne: { x: cropArea.x + cropArea.width, y: cropArea.y },
          sw: { x: cropArea.x, y: cropArea.y + cropArea.height },
          se: { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height },
          n: { x: cropArea.x + cropArea.width / 2, y: cropArea.y },
          s: { x: cropArea.x + cropArea.width / 2, y: cropArea.y + cropArea.height },
          w: { x: cropArea.x, y: cropArea.y + cropArea.height / 2 },
          e: { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height / 2 },
        };

        let handle: string | null = null;
        for (const [key, pos] of Object.entries(handles)) {
          if (
            Math.abs(x - pos.x) < handleSize &&
            Math.abs(y - pos.y) < handleSize
          ) {
            handle = key;
            break;
          }
        }

        if (handle) {
          setIsResizing(true);
          setResizeHandle(handle);
        } else if (isInCropArea) {
          setIsDragging(true);
        } else {
          setIsMovingImage(true);
        }

        setDragStart({ x, y });
        e.preventDefault();
      },
      [cropArea]
    );

    const handleMouseMove = React.useCallback(
      (e: MouseEvent | TouchEvent) => {
        if (!containerRef.current) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        if (isResizing && resizeHandle) {
          // 调整裁切框大小
          let newCropArea = { ...cropArea };

          switch (resizeHandle) {
            case 'nw':
              newCropArea.x = Math.max(0, cropArea.x + deltaX);
              newCropArea.y = Math.max(0, cropArea.y + deltaY);
              newCropArea.width = cropArea.width - deltaX;
              newCropArea.height = cropArea.height - deltaY;
              break;
            case 'ne':
              newCropArea.y = Math.max(0, cropArea.y + deltaY);
              newCropArea.width = cropArea.width + deltaX;
              newCropArea.height = cropArea.height - deltaY;
              break;
            case 'sw':
              newCropArea.x = Math.max(0, cropArea.x + deltaX);
              newCropArea.width = cropArea.width - deltaX;
              newCropArea.height = cropArea.height + deltaY;
              break;
            case 'se':
              newCropArea.width = cropArea.width + deltaX;
              newCropArea.height = cropArea.height + deltaY;
              break;
            case 'n':
              newCropArea.y = Math.max(0, cropArea.y + deltaY);
              newCropArea.height = cropArea.height - deltaY;
              if (mode === 'avatar') {
                // 头像模式：保持1:1，调整宽度
                newCropArea.width = newCropArea.height;
                newCropArea.x = cropArea.x + (cropArea.width - newCropArea.width) / 2;
              } else if (mode === 'custom' && actualAspectRatio) {
                // 固定比例：上下边仅改高度，宽度按比例同步，水平居中
                newCropArea.width = newCropArea.height * actualAspectRatio;
                newCropArea.x = cropArea.x + cropArea.width / 2 - newCropArea.width / 2;
              }
              break;
            case 's':
              newCropArea.height = cropArea.height + deltaY;
              if (mode === 'avatar') {
                // 头像模式：保持1:1，调整宽度
                newCropArea.width = newCropArea.height;
                newCropArea.x = cropArea.x + (cropArea.width - newCropArea.width) / 2;
              } else if (mode === 'custom' && actualAspectRatio) {
                newCropArea.width = newCropArea.height * actualAspectRatio;
                newCropArea.x = cropArea.x + cropArea.width / 2 - newCropArea.width / 2;
              }
              break;
            case 'w':
              newCropArea.x = Math.max(0, cropArea.x + deltaX);
              newCropArea.width = cropArea.width - deltaX;
              if (mode === 'avatar') {
                // 头像模式：保持1:1，调整高度
                newCropArea.height = newCropArea.width;
                newCropArea.y = cropArea.y + (cropArea.height - newCropArea.height) / 2;
              } else if (mode === 'custom' && actualAspectRatio) {
                // 固定比例：左右边仅改宽度，高度按比例同步，垂直居中
                newCropArea.height = newCropArea.width / actualAspectRatio;
                newCropArea.y = cropArea.y + cropArea.height / 2 - newCropArea.height / 2;
              }
              break;
            case 'e':
              newCropArea.width = cropArea.width + deltaX;
              if (mode === 'avatar') {
                // 头像模式：保持1:1，调整高度
                newCropArea.height = newCropArea.width;
                newCropArea.y = cropArea.y + (cropArea.height - newCropArea.height) / 2;
              } else if (mode === 'custom' && actualAspectRatio) {
                newCropArea.height = newCropArea.width / actualAspectRatio;
                newCropArea.y = cropArea.y + cropArea.height / 2 - newCropArea.height / 2;
              }
              break;
          }

          // 应用宽高比限制
          // 头像：四角由此处统一约束；边中点已在 case 内保持 1:1
          // custom + 固定比例：四角由此处约束；边中点已在 case 内保持比例
          if (actualAspectRatio) {
            if (mode === 'avatar' && ['nw', 'ne', 'sw', 'se'].includes(resizeHandle)) {
              // 头像模式：只有四个角的手柄需要应用宽高比限制
              const currentAspect = newCropArea.width / newCropArea.height;
              if (currentAspect > actualAspectRatio) {
                newCropArea.height = newCropArea.width / actualAspectRatio;
              } else {
                newCropArea.width = newCropArea.height * actualAspectRatio;
              }
            } else if (mode === 'custom' && ['nw', 'ne', 'sw', 'se'].includes(resizeHandle)) {
              const currentAspect = newCropArea.width / newCropArea.height;
              if (currentAspect > actualAspectRatio) {
                newCropArea.height = newCropArea.width / actualAspectRatio;
              } else {
                newCropArea.width = newCropArea.height * actualAspectRatio;
              }
            }
          }

          // 限制最小尺寸
          const minSize = 50;
          if (newCropArea.width < minSize) {
            newCropArea.width = minSize;
            if (actualAspectRatio) {
              newCropArea.height = minSize / actualAspectRatio;
            }
          }
          if (newCropArea.height < minSize) {
            newCropArea.height = minSize;
            if (actualAspectRatio) {
              newCropArea.width = minSize * actualAspectRatio;
            }
          }

          // 限制在容器内
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          newCropArea.x = Math.max(0, Math.min(newCropArea.x, containerWidth - newCropArea.width));
          newCropArea.y = Math.max(0, Math.min(newCropArea.y, containerHeight - newCropArea.height));

          setCropArea(newCropArea);
          setDragStart({ x, y });
        } else if (isDragging) {
          // 移动裁切框
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          const newX = Math.max(0, Math.min(cropArea.x + deltaX, containerWidth - cropArea.width));
          const newY = Math.max(0, Math.min(cropArea.y + deltaY, containerHeight - cropArea.height));

          setCropArea((prev) => ({
            ...prev,
            x: newX,
            y: newY,
          }));
          setDragStart({ x, y });
        } else if (isMovingImage) {
          // 移动图片
          setImagePosition((prev) => ({
            x: prev.x + deltaX,
            y: prev.y + deltaY,
          }));
          setDragStart({ x, y });
        }
      },
      [
        isResizing,
        isDragging,
        isMovingImage,
        resizeHandle,
        cropArea,
        dragStart,
        actualAspectRatio,
        mode,
      ]
    );

    const handleMouseUp = React.useCallback(() => {
      setIsDragging(false);
      setIsResizing(false);
      setIsMovingImage(false);
      setResizeHandle(null);
    }, []);

    // 视口内禁止缩放：滚轮仅阻止页面滚动，不改变图片缩放。
    const handleWheel = React.useCallback(
      (e: WheelEvent) => {
        e.preventDefault();
      },
      []
    );

    // 绑定全局事件
    React.useEffect(() => {
      if (isDragging || isResizing || isMovingImage) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleMouseMove);
        document.addEventListener('touchend', handleMouseUp);

        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          document.removeEventListener('touchmove', handleMouseMove);
          document.removeEventListener('touchend', handleMouseUp);
        };
      }
    }, [isDragging, isResizing, isMovingImage, handleMouseMove, handleMouseUp]);

    // 滚轮：非 passive 监听才能 preventDefault，避免「Unable to preventDefault inside passive event listener」
    React.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    // touchstart 须非 passive 才能 preventDefault，否则拖拽时会触发页面滚动
    React.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const onTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        handleMouseDown(e as unknown as React.TouchEvent);
      };
      el.addEventListener('touchstart', onTouchStart, { passive: false });
      return () => el.removeEventListener('touchstart', onTouchStart);
    }, [handleMouseDown]);

    // 键盘事件
    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!containerRef.current) return;

        switch (e.key) {
          case 'ArrowLeft':
            setCropArea((prev) => ({
              ...prev,
              x: Math.max(0, prev.x - keyboardStep),
            }));
            break;
          case 'ArrowRight':
            setCropArea((prev) => {
              const maxX = containerRef.current!.clientWidth - prev.width;
              return {
                ...prev,
                x: Math.min(maxX, prev.x + keyboardStep),
              };
            });
            break;
          case 'ArrowUp':
            setCropArea((prev) => ({
              ...prev,
              y: Math.max(0, prev.y - keyboardStep),
            }));
            break;
          case 'ArrowDown':
            setCropArea((prev) => {
              const maxY = containerRef.current!.clientHeight - prev.height;
              return {
                ...prev,
                y: Math.min(maxY, prev.y + keyboardStep),
              };
            });
            break;
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [keyboardStep]);

    // 对外缩放接口保留但在裁切视口内禁用缩放行为。
    const handleZoomIn = React.useCallback(() => {
      return;
    }, []);

    const handleZoomOut = React.useCallback(() => {
      return;
    }, []);

    // 旋转控制
    const handleRotateCCW = React.useCallback(() => {
      setRotation((prev) => (prev - 90) % 360);
    }, []);

    const handleRotateCW = React.useCallback(() => {
      setRotation((prev) => (prev + 90) % 360);
    }, []);

    // 通知裁切变化
    React.useEffect(() => {
      if (onCropChange && cropArea.width > 0 && cropArea.height > 0) {
        onCropChange(cropArea);
      }
    }, [cropArea, onCropChange]);

    // 裁切区域变化时生成实时预览 data URL（使用 imageRect 与选择框一致）
    React.useEffect(() => {
      if (!onCropPreview || cropArea.width <= 0 || cropArea.height <= 0 || !imageLoaded) {
        return;
      }
      let cancelled = false;
      const rafId = requestAnimationFrame(() => {
        if (!containerRef.current || !imageRef.current) return;
        const img = imageRef.current;
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const imageRect = img.getBoundingClientRect();
        const imageScale = imageRect.width / img.naturalWidth;
        const cropX = (cropArea.x - (imageRect.left - containerRect.left)) / imageScale;
        const cropY = (cropArea.y - (imageRect.top - containerRect.top)) / imageScale;
        const cropWidth = cropArea.width / imageScale;
        const cropHeight = cropArea.height / imageScale;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        const sourceImg = new Image();
        sourceImg.crossOrigin = 'anonymous';
        sourceImg.src = image;
        sourceImg.onload = () => {
          if (cancelled) return;
          if (rotation !== 0) {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) return;
            const angle = (rotation * Math.PI) / 180;
            const cos = Math.abs(Math.cos(angle));
            const sin = Math.abs(Math.sin(angle));
            const rotatedWidth = sourceImg.width * cos + sourceImg.height * sin;
            const rotatedHeight = sourceImg.width * sin + sourceImg.height * cos;
            tempCanvas.width = rotatedWidth;
            tempCanvas.height = rotatedHeight;
            tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
            tempCtx.rotate(angle);
            tempCtx.drawImage(sourceImg, -sourceImg.width / 2, -sourceImg.height / 2);
            ctx.drawImage(tempCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
          } else {
            ctx.drawImage(sourceImg, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
          }
          const dataUrl = canvas.toDataURL(outputFormat, quality);
          onCropPreview(dataUrl);
        };
        sourceImg.onerror = () => {};
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    }, [cropArea, zoom, imagePosition, rotation, image, imageLoaded, onCropPreview, outputFormat, quality]);

    // 完成裁切
    const handleComplete = React.useCallback(async () => {
      if (!imageRef.current || !containerRef.current || cropArea.width === 0) {
        return;
      }

      try {
        const img = imageRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('无法创建canvas上下文');
        }

        // 计算实际裁切区域（使用 getBoundingClientRect 与预览一致，避免放大时偏差）
        const containerRect = containerRef.current.getBoundingClientRect();
        const imageRect = img.getBoundingClientRect();
        const imageScale = imageRect.width / img.naturalWidth;

        const cropX = (cropArea.x - (imageRect.left - containerRect.left)) / imageScale;
        const cropY = (cropArea.y - (imageRect.top - containerRect.top)) / imageScale;
        const cropWidth = cropArea.width / imageScale;
        const cropHeight = cropArea.height / imageScale;

        // 设置canvas尺寸
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // 如果有旋转，需要先旋转图片
        if (rotation !== 0) {
          const sourceImg = new Image();
          sourceImg.crossOrigin = 'anonymous';
          sourceImg.src = image;

          await new Promise<void>((resolve, reject) => {
            sourceImg.onload = () => {
              const tempCanvas = document.createElement('canvas');
              const tempCtx = tempCanvas.getContext('2d');
              if (!tempCtx) {
                reject(new Error('无法创建临时canvas上下文'));
                return;
              }

              const angle = (rotation * Math.PI) / 180;
              const cos = Math.abs(Math.cos(angle));
              const sin = Math.abs(Math.sin(angle));
              const rotatedWidth = sourceImg.width * cos + sourceImg.height * sin;
              const rotatedHeight = sourceImg.width * sin + sourceImg.height * cos;

              tempCanvas.width = rotatedWidth;
              tempCanvas.height = rotatedHeight;

              tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
              tempCtx.rotate(angle);
              tempCtx.drawImage(
                sourceImg,
                -sourceImg.width / 2,
                -sourceImg.height / 2
              );

              // 从旋转后的图片中裁切
              ctx.drawImage(
                tempCanvas,
                cropX,
                cropY,
                cropWidth,
                cropHeight,
                0,
                0,
                canvas.width,
                canvas.height
              );

              resolve();
            };
            sourceImg.onerror = reject;
          });
        } else {
          const sourceImg = new Image();
          sourceImg.crossOrigin = 'anonymous';
          sourceImg.src = image;

          await new Promise<void>((resolve, reject) => {
            sourceImg.onload = () => {
              ctx.drawImage(
                sourceImg,
                cropX,
                cropY,
                cropWidth,
                cropHeight,
                0,
                0,
                canvas.width,
                canvas.height
              );
              resolve();
            };
            sourceImg.onerror = reject;
          });
        }

        // 头像模式：裁切正方形区域（不转换为圆形）
        // 转换为blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              throw new Error('图片转换失败');
            }
            const file = new File(
              [blob],
              `cropped-${Date.now()}.${outputFormat.split('/')[1]}`,
              {
                type: outputFormat,
                lastModified: Date.now(),
              }
            );
            onComplete?.(file);
          },
          outputFormat,
          quality
        );
      } catch (error) {
        console.error('图片裁切失败:', error);
        throw error;
      }
    }, [
      image,
      rotation,
      mode,
      outputFormat,
      quality,
      onComplete,
      zoom,
      imagePosition,
      cropArea,
    ]);

    const getNormalizedCropRect = React.useCallback((): ImageCropperNormRect | null => {
      if (!imageRef.current || !containerRef.current || cropArea.width <= 0 || cropArea.height <= 0) {
        return null;
      }
      const rot = ((rotation % 360) + 360) % 360;
      if (rot > 0.01 && rot < 359.99) return null;

      const img = imageRef.current;
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return null;

      const containerRect = containerRef.current.getBoundingClientRect();
      const imageRect = img.getBoundingClientRect();
      const imageScale = imageRect.width / nw;
      if (!Number.isFinite(imageScale) || imageScale <= 0) return null;

      const cropX = (cropArea.x - (imageRect.left - containerRect.left)) / imageScale;
      const cropY = (cropArea.y - (imageRect.top - containerRect.top)) / imageScale;
      const cropWidth = cropArea.width / imageScale;
      const cropHeight = cropArea.height / imageScale;

      const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
      let x = clamp(cropX / nw, 0, 1);
      let y = clamp(cropY / nh, 0, 1);
      let w = clamp(cropWidth / nw, 0.001, 1);
      let h = clamp(cropHeight / nh, 0.001, 1);
      if (x + w > 1) w = 1 - x;
      if (y + h > 1) h = 1 - y;
      if (w < 0.001 || h < 0.001) return null;
      return { x, y, w, h };
    }, [cropArea, rotation]);

    // 暴露方法给外部
    React.useImperativeHandle(
      ref,
      () => ({
        zoomIn: handleZoomIn,
        zoomOut: handleZoomOut,
        rotateCCW: handleRotateCCW,
        rotateCW: handleRotateCW,
        complete: handleComplete,
        getNormalizedCropRect,
      }),
      [handleZoomIn, handleZoomOut, handleRotateCCW, handleRotateCW, handleComplete, getNormalizedCropRect],
    );

    return (
      <div
        ref={containerDivRef}
        className={cn('flex flex-col h-full', className)}
      >
        {/* 裁切区域 */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden bg-gray-900 cursor-move"
          onMouseDown={handleMouseDown}
        >
          {/* 遮罩层 - 选择框外遮罩，选择框内透明 */}
          {cropArea.width > 0 && cropArea.height > 0 && containerRef.current && (
            <>
              {mode === 'avatar' ? (
                // 圆形遮罩：使用 SVG mask 实现反向遮罩（白色显示遮罩，黑色挖空）
                <>
                  <svg
                    className="absolute inset-0 pointer-events-none z-[5]"
                    style={{ width: '100%', height: '100%' }}
                  >
                    <defs>
                      <mask id={`circle-mask-avatar-${Math.round(cropArea.x)}-${Math.round(cropArea.y)}-${Math.round(cropArea.width)}-${Math.round(cropArea.height)}`}>
                        <rect width="100%" height="100%" fill="white" />
                        <circle
                          cx={cropArea.x + cropArea.width / 2}
                          cy={cropArea.y + cropArea.height / 2}
                          r={Math.min(cropArea.width, cropArea.height) / 2}
                          fill="black"
                        />
                      </mask>
                    </defs>
                    <rect
                      width="100%"
                      height="100%"
                      fill="rgba(0, 0, 0, 0.75)"
                      mask={`url(#circle-mask-avatar-${Math.round(cropArea.x)}-${Math.round(cropArea.y)}-${Math.round(cropArea.width)}-${Math.round(cropArea.height)})`}
                    />
                  </svg>
                </>
              ) : (
                // 矩形遮罩：使用四个矩形包围选择框
                <>
                  {/* 上方遮罩 */}
                  {cropArea.y > 0 && (
                    <div
                      className="absolute pointer-events-none z-[5] bg-black/75"
                      style={{
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: `${cropArea.y}px`,
                      }}
                    />
                  )}
                  {/* 下方遮罩 */}
                  {cropArea.y + cropArea.height < containerRef.current.clientHeight && (
                    <div
                      className="absolute pointer-events-none z-[5] bg-black/75"
                      style={{
                        left: 0,
                        top: `${cropArea.y + cropArea.height}px`,
                        width: '100%',
                        height: `${containerRef.current.clientHeight - cropArea.y - cropArea.height}px`,
                      }}
                    />
                  )}
                  {/* 左侧遮罩 */}
                  {cropArea.x > 0 && (
                    <div
                      className="absolute pointer-events-none z-[5] bg-black/75"
                      style={{
                        left: 0,
                        top: `${cropArea.y}px`,
                        width: `${cropArea.x}px`,
                        height: `${cropArea.height}px`,
                      }}
                    />
                  )}
                  {/* 右侧遮罩 */}
                  {cropArea.x + cropArea.width < containerRef.current.clientWidth && (
                    <div
                      className="absolute pointer-events-none z-[5] bg-black/75"
                      style={{
                        left: `${cropArea.x + cropArea.width}px`,
                        top: `${cropArea.y}px`,
                        width: `${containerRef.current.clientWidth - cropArea.x - cropArea.width}px`,
                        height: `${cropArea.height}px`,
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* 图片 */}
          <img
            ref={imageRef}
            src={image}
            alt="Crop"
            className="absolute select-none"
            onLoad={handleImageLoad}
            style={{
              transform: imageLoaded
                ? `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoom}) rotate(${rotation}deg)`
                : 'none',
              transformOrigin: 'center center',
              maxWidth: 'none',
              maxHeight: 'none',
              width: imageLoaded ? `${imageSize.width}px` : 'auto',
              height: imageLoaded ? `${imageSize.height}px` : 'auto',
              top: '50%',
              left: '50%',
              marginTop: imageLoaded ? `-${imageSize.height / 2}px` : '-50%',
              marginLeft: imageLoaded ? `-${imageSize.width / 2}px` : '-50%',
              zIndex: 1,
              opacity: imageLoaded ? 1 : 0,
            }}
            draggable={false}
          />

          {/* 裁切框 */}
          {cropArea.width > 0 && cropArea.height > 0 && (
            <div
              className={cn(
                'absolute z-10',
                isDragging ? 'cursor-move' : 'cursor-default',
                mode === 'avatar' ? 'rounded-md' : 'rounded-md'
              )}
              style={{
                left: `${cropArea.x}px`,
                top: `${cropArea.y}px`,
                width: `${cropArea.width}px`,
                height: `${cropArea.height}px`,
                pointerEvents: 'auto',
                border: '1px dashed white',
              }}
            >
              {/* 头像模式的圆形边框（仅显示圆形边框，不遮挡内容） */}
              {mode === 'avatar' && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: '50%',
                    border: '1px dashed white',
                  }}
                />
              )}
              
              {/* 调整手柄 - 所有模式都显示8个手柄 */}
              <>
                {/* 四个角的手柄 */}
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -top-1.5 -left-1.5 cursor-nwse-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -top-1.5 -right-1.5 cursor-nesw-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -bottom-1.5 -left-1.5 cursor-nesw-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -bottom-1.5 -right-1.5 cursor-nwse-resize"
                  style={{ zIndex: 20 }}
                />
                {/* 中间的手柄 */}
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -top-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -bottom-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -left-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize"
                  style={{ zIndex: 20 }}
                />
                <div
                  className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm -right-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize"
                  style={{ zIndex: 20 }}
                />
              </>
            </div>
          )}
        </div>

      </div>
    );
  }
);

ImageCropper.displayName = 'ImageCropper';
